import {createReducer, on} from '@ngrx/store';
import {
  addSearchConditions,
  addSearchParameters,
  removeSearchParameters,
  removeSearchConditions,
  modifyGlobalConfig,
  removeGlobalConfig,
  loadGlobalConfigs,
  loadGlobalConfigsSuccess,
  loadGlobalConfigsFailure,
  addGlobalConfigSuccess,
  addGlobalConfigFailure,
  modifyGlobalConfigSuccess,
  modifyGlobalConfigFailure,
  removeGlobalConfigSuccess,
  removeGlobalConfigFailure,
  setGlobalConfigService,
  addStockSize,
  changeStockSizePriority,
  removeStockSize,
  clearGlobalConfigState,
  changeReportColumnPriority,
  addReportColumn,
  removeReportColumn,
  modifyProcessPriorities,
  setHighlightId
} from "./global-config.action";
import {StateStatus} from "../../../shared/models/state-status.models";

import {
  addSearchCondition,
  addSearchParameter, removeSearchCondition,
  removeSearchParameter
} from "../../utils/search-condition-utils.service";
import {removeStockSizeOptions} from "../../utils/stock-size-utils.service";
import {GlobalConfig} from "../../models/global-config.models";
import {v4 as uuidv4} from 'uuid';


export interface GlobalConfigState {
  globalConfigs: GlobalConfig[];
  globalConfigService: string;
  globalConfigLabel: string;
  error: string | null;
  status: StateStatus;
  highlightId: number | null;
}

export const initialState: GlobalConfigState = {
  globalConfigs: [],
  globalConfigService: '',
  globalConfigLabel: '',
  error: null,
  status: 'pending' as StateStatus,
  highlightId: null,
};

export const globalConfigReducer = createReducer(
  initialState,
    on(setHighlightId, (state, { id }) => ({
      ...state,
      highlightId: id,
    })),
  on(clearGlobalConfigState, (state) => ({
    ...state,
    status: 'loading' as StateStatus,
    globalConfigs: [],
    globalConfigService: '',
    highlightId: null,
    error: null,
  })),
  on(addSearchConditions, (state, { globalConfigId }) => ({
    ...state,
    status: 'saveSuccess' as StateStatus,
    globalConfigs: addSearchCondition(state.globalConfigs, globalConfigId)
  })),
  on(removeSearchConditions, (state, { globalConfigId, searchConditionId }) => ({
    ...state,
    status: 'saveSuccess' as StateStatus,
    globalConfigs: removeSearchCondition(state.globalConfigs, globalConfigId, searchConditionId)
  })),
  on(removeStockSize, (state, { globalConfigId, stockSizeId }) => ({
    ...state,
    status: 'saveSuccess' as StateStatus,
    globalConfigs: removeStockSizeOptions(state.globalConfigs, globalConfigId, stockSizeId)
  })),
  on(addSearchParameters, (state, { globalConfigId, searchConditionId }) => ({
    ...state,
    status: 'saveSuccess' as StateStatus,
    globalConfigs: addSearchParameter(state.globalConfigs, globalConfigId, searchConditionId)
  })),
  on(removeSearchParameters, (state, { globalConfigId, searchConditionId, searchParameterId }) => ({
    ...state,
    status: 'saveSuccess' as StateStatus,
    globalConfigs: removeSearchParameter(state.globalConfigs, globalConfigId, searchConditionId, searchParameterId)
  })),
  on(modifyGlobalConfig, (state, { globalConfig }) => ({
    ...state,
    status: 'modifying' as StateStatus,
    globalConfigs: state.globalConfigs.map(originalGlobalConfig => {
      if (originalGlobalConfig.id === globalConfig.id){
        return globalConfig;
      }
      return originalGlobalConfig;
    })
  })),
  on(removeGlobalConfig, (state, { globalConfigId }) => ({
    ...state,
    status: 'removing' as StateStatus,
  })),
  on(loadGlobalConfigs, (state) => ({ ...state, status: 'loading' as StateStatus })),
  on(loadGlobalConfigsSuccess, (state, { globalConfigs }) => ({
    ...state,
    globalConfigs: globalConfigs,
    error: null,
    status: 'loadSuccess' as StateStatus,
  })),
  on(loadGlobalConfigsFailure, (state, { error }) => ({
    ...state,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(setGlobalConfigService, (state, { globalConfigService, globalConfigLabel }) => ({
    ...state,
    error: null,
    status: 'pending' as StateStatus,
    globalConfigService: globalConfigService,
    globalConfigLabel: globalConfigLabel,

  })),
  on(addGlobalConfigSuccess, (state, { globalConfig }) => ({
    ...state,
    status: 'addSuccess' as StateStatus,
    globalConfigs: [
      ...state.globalConfigs,
      globalConfig,
    ]
  })),
  on(addGlobalConfigFailure, (state, { error }) => ({
    ...state,
    globalConfigs: state.globalConfigs,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(modifyGlobalConfigSuccess, (state, { globalConfigs }) => ({
    ...state,
    globalConfigs: state.globalConfigs,
    error: null,
    status: 'saveSuccess' as StateStatus,
  })),
  on(modifyGlobalConfigFailure, (state, { error }) => ({
    ...state,
    globalConfigs: state.globalConfigs,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(removeGlobalConfigSuccess, (state, { globalConfigId }) => ({
    ...state,
    globalConfigs: state.globalConfigs.filter((globalConfig) => globalConfig.id !== globalConfigId),
    error: null,
    status: 'removed' as StateStatus,
  })),
  on(removeGlobalConfigFailure, (state, { error }) => ({
    ...state,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(addStockSize, (state, { globalConfigId, stockSize }) => ({
    ...state,
    status: 'saveSuccess' as StateStatus,
    globalConfigs: state.globalConfigs.map((globalConfig: any) => globalConfig.id === globalConfigId ? {
      ...globalConfig,
      stockSizes: [stockSize, ...globalConfig.stockSizes]
    } as GlobalConfig: globalConfig
    )
  })),
  on(changeStockSizePriority, (state, { globalConfigId, stockSize }) => ({
    ...state,
    status: 'modifying' as StateStatus,
    globalConfigs: state.globalConfigs.map((globalConfig: any) => globalConfig.id === globalConfigId ? {
        ...globalConfig,
      stockSizes: stockSize
      } as GlobalConfig: globalConfig
    )
  })),
  on(modifyProcessPriorities, (state, { priorities }) => {
    const updated = state.globalConfigs.map(globalConfig => {
      const found = priorities.find(p => Number(p.id) === Number(globalConfig.id));
      return found ? { ...globalConfig, priority: found.priority } as GlobalConfig : globalConfig;
    });
    return {
      ...state,
      status: 'modifying' as StateStatus,
      globalConfigs: [...updated].sort((globalConfig1: any, globalConfig2: any) => globalConfig1.priority - globalConfig2.priority),
    };
  }),
  on(changeReportColumnPriority, (state, { globalConfigId, reportColumns }) => ({
    ...state,
    status: 'modifying' as StateStatus,
    globalConfigs: state.globalConfigs.map((globalConfig: any) => globalConfig.id === globalConfigId ? {
        ...globalConfig,
        reportColumns: reportColumns
      } as GlobalConfig: globalConfig
    )
  })),
  on(addReportColumn, (state, { globalConfigId, reportColumn }) => ({
    ...state,
    status: 'addSuccess' as StateStatus,
    globalConfigs: state.globalConfigs.map((globalConfig: any) => globalConfig.id === globalConfigId ? {
        ...globalConfig,
        reportColumns: [{...reportColumn, id: uuidv4()}, ...globalConfig.reportColumns]
      } as GlobalConfig: globalConfig
    )
  })),
  on(removeReportColumn, (state, { globalConfigId, reportColumnId }) => ({
    ...state,
    status: 'removeSuccess' as StateStatus,
    globalConfigs: state.globalConfigs.map((globalConfig: any) => globalConfig.id === globalConfigId ? {
        ...globalConfig,
        reportColumns: globalConfig.reportColumns.filter(rc => rc.id !== reportColumnId)
      } as GlobalConfig: globalConfig
    )
  })),


);
